import { useState, useEffect } from "react";

const PER_PAGE = 9;

interface Winner {
  id: number;
  name: string;
  school_name: string;
  city: string;
  profile_image: string | null;
}

interface LeaderboardResponse {
  winners: Winner[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export function Leaderboard() {
  const [winners, setWinners] = useState<Winner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("per_page", String(PER_PAGE));
    if (search.trim()) params.set("search", search.trim());

    fetch(`/api/leaderboard?${params}`)
      .then((r) => r.json())
      .then((data: LeaderboardResponse) => {
        setWinners(data.winners);
        setTotalPages(data.total_pages);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page, search]);

  function goTo(p: number) {
    setPage(Math.max(1, Math.min(p, totalPages)));
  }

  function handleSearch(val: string) {
    setSearch(val);
    setPage(1);
  }

  return (
    <main className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <div className="text-6xl mb-4">🏆</div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#5716a8] font-poppins mb-2">
          Winner's Circle
        </h1>
        <p className="text-purple-600/60 text-lg">Congratulations to all our champions! 🎉</p>
      </div>

      <div className="flex justify-center mb-8">
        <div className="relative w-full max-w-sm">
          <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search winners by name..."
            className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-amber-200 bg-white/80 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all"
          />
          {search && (
            <button
              onClick={() => handleSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="text-5xl mb-4 animate-spin-slow">⚽</div>
          <p className="text-purple-400/60">Loading winners...</p>
        </div>
      ) : total === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4 opacity-50">🏆</div>
          <p className="text-gray-400 text-lg">No winners announced yet. Stay tuned!</p>
          <p className="text-gray-300 text-sm mt-2">Winners will appear here once selected.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {winners.map((winner) => (
            <div key={winner.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl p-5 text-center border border-amber-100 transition-all duration-300 hover:-translate-y-1">

              <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden bg-gradient-to-br from-purple-100 to-[#985ae6]/30 ring-2 ring-amber-300 ring-offset-2">
                {winner.profile_image ? (
                  <img src={winner.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-[#5716a8]">
                    {winner.name.charAt(0)}
                  </div>
                )}
              </div>

              <h3 className="font-bold text-gray-900 text-lg leading-tight">{winner.name}</h3>

              <div className="flex items-center justify-center gap-1.5 text-sm text-gray-500 mt-2">
                <i className="fa-solid fa-school text-purple-500/60 text-xs" />
                <span className="truncate">{winner.school_name}</span>
              </div>
            </div>
          ))}
        </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-10">
              <button
                onClick={() => goTo(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <i className="fa-solid fa-chevron-left text-xs" />
                Previous
              </button>

              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>

              <button
                onClick={() => goTo(page + 1)}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Next
                <i className="fa-solid fa-chevron-right text-xs" />
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
